import React from "react";
import {
  MotifIcon,
  MotifTruncate,
  MotifTooltip,
  MotifChip,
} from "@ey-xd/motif-react";
import { SKU } from "../../../../store/slices/CheckoutSlice";
import {
  IconoirNavArrowUp,
  IconoirNavArrowDown,
  IconoirWarningCircle,
} from "@ey-xd/motif-icon";
import {
  calculateTotalCost,
  getCostInterval,
  getDurationType,
  getUnitsUsedForPeriod,
  getCostIntervalForCostMetric,
} from "../../CheckoutHelper";
import {
  variableCostInfo,
  fixedCostInfo,
  extensionCostInfo,
} from "../../../../utils/skuTable";

interface ProductAccordianProps {
  skuDetails: SKU[];
  productName: string;
  showEngagementColumn?: boolean;
  showRetiredError?: boolean;
  isOrderpipelineOrderTab?: boolean;
  showQuantityColumn?: boolean;
  isCheckoutPage?: boolean;
  screenWidth?: number;
}
export function ProductAccordian({
  skuDetails,
  productName,
  showEngagementColumn = false,
  isOrderpipelineOrderTab = false,
  showRetiredError = false,
  showQuantityColumn = true,
  isCheckoutPage = false,
  screenWidth,
}: ProductAccordianProps) {
  const [isExpand, setIsExpand] = React.useState(true);
  const totalCostColorStyle = {
    color: isOrderpipelineOrderTab ? "#FFE600" : "",
  };
  const getNormalizedText = (value: string | null | undefined) => {
    if (typeof value !== "string") return "";
    const trimmedValue = value.trim();
    if (/^[-–—]+$/.test(trimmedValue)) return "";
    return trimmedValue;
  };

  const getEngagementCodeText = (entity: any) => {
    const code = getNormalizedText(entity?.EngagementId);
    return code || "--";
  };

  const getEngagementNameText = (entity: any) => {
    const name = getNormalizedText(entity?.EngagementName);
    return name || "--";
  };

  const getSkuTypeMeta = (skuType: string | null | undefined) => {
    const normalizedSkuType = getNormalizedText(skuType).toLowerCase();

    if (normalizedSkuType.includes("fixed")) {
      return {
        className: "fixed",
        label: "Fixed",
        title: "Fixed cost SKU",
        description:
          "This is a fixed SKU with a standard unit cost. Charges are based on the units you select each month (billed the 1st of the following month or week billed on the Saturday of the following week).",
      };
    }

    if (normalizedSkuType.includes("variable")) {
      return {
        className: "variable",
        label: "Variable",
        title: "Important notice - Variable cost SKU",
        description:
          "This is a variable SKU with an estimated cost. Actual charges depend on your consumption and are billed at the end of each billing cycle during active periods.",
      };
    }

    return {
      className: "extension",
      label: "Extension",
      title: "Extension SKU",
      description:
        "The “Extension” label appears on SKUs that have been extended so you can easily identify them.",
    };
  };

  const getTooltipContent = (skuType: string | null | undefined) => {
    const normalizedSkuType = getNormalizedText(skuType).toLowerCase();
    if (normalizedSkuType.includes("fixed")) {
      return fixedCostInfo();
    }
    if (normalizedSkuType.includes("variable")) {
      return variableCostInfo();
    }

    return extensionCostInfo();
  };

  const renderSkuTypeBadge = (skuType: string | null | undefined) => {
    const skuTypeMeta = getSkuTypeMeta(skuType);
    return (
      <MotifTooltip
        contentClassName="tooltip-content"
        id={"tooltip"}
        hideArrow={true}
        allowClick={false}
        placement="top"
        trigger={
          <MotifChip
            className={`sku-badge ${skuTypeMeta.className}`}
            onClick={() => {}}
            disabled={false}
          >
            {skuTypeMeta.label}
          </MotifChip>
        }
      >
        {getTooltipContent(skuType)}
      </MotifTooltip>
    );
  };

  const getTotalOrderAmount = (SkuDetails: any) => {
    if (!SkuDetails || SkuDetails === null) {
      return 0;
    }
    if (SkuDetails && SkuDetails !== null) {
      let totalAmount = 0;
      SkuDetails.forEach((sku: any) => {
        const quantity = sku.OrderQuantity || 0;
        const cost = sku.CostMetricCost || 0;
        const duration = sku.OrderInterval || 0;
        totalAmount += cost * quantity * duration;
        if (sku.SkuRelation && sku.SkuRelation.length > 0) {
          sku.SkuRelation.forEach((relation: any) => {
            const quantity = relation.OrderQuantity || 0;
            const cost = relation.CostMetricCost || 0;
            const duration = relation.OrderInterval || 0;
            totalAmount += cost * quantity * duration;
          });
        }
      });
      return totalAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }
  };

  if (!skuDetails || skuDetails.length === 0) {
    return <div>No SKUs available</div>;
  }

  const getDuration = (sku: any) => {
    if (sku.CostIntervalCd == "onetime") {
      return (
        <div className="table-cell">
          <div>--</div>
          <div className="sku-number">Not Applicable</div>
        </div>
      );
    } else {
      return (
        <div className="table-cell">
          <div>{sku.OrderInterval}</div>
          <div className="sku-number">
            {getDurationType(sku.CostIntervalCd)}
          </div>
        </div>
      );
    }
  };

  const handleAccordianClick = (e: React.MouseEvent<HTMLUListElement>) => {
    const parentSku = e.currentTarget.closest(".parent-sku");
    if (parentSku) {
      parentSku.classList.toggle("inactive");
      const inactive = parentSku.classList.contains("inactive");
      setIsExpand(!inactive);
    }
  };

  const renderProductRow = (detail: any) => {
    return (
      <tr key={detail.ProductName} className="parent-row">
        <td className={isCheckoutPage ? "sku-col" : ""}>
          <ul className="tree parent-sku" onClick={handleAccordianClick}>
            <li className="tree-trigger">
              <div className="table-cell">
                <div
                  className="icon"
                  style={{ display: isExpand ? "none" : "block" }}
                >
                  <MotifIcon iconFunction={IconoirNavArrowUp} size="20" />
                </div>
                <div
                  className="icon"
                  style={{ display: !isExpand ? "none" : "block" }}
                >
                  <MotifIcon iconFunction={IconoirNavArrowDown} size="20" />
                </div>
                <div className="title">
                  <MotifTruncate
                    maxLines={1}
                    maxWidth={
                      screenWidth ? Math.round((screenWidth - 200) * 0.2) : 400
                    }
                  >
                    {productName}
                  </MotifTruncate>
                </div>
              </div>
            </li>
            <ul>
              {detail.map((sku: any, index: number) => {
                const skuError = sku?.StatusCd === "sku_retired";
                if (sku.SkuRelation.length === 0) {
                  return (
                    <li key={index}>
                      <div
                        className={`sku-cell ${
                          skuError && showRetiredError ? "sku-error" : ""
                        }`}
                      >
                        <div className="sku-cell-number">
                          {skuError && showRetiredError && (
                            <MotifIcon
                              src={IconoirWarningCircle}
                              className="warning-icon"
                            />
                          )}
                          <MotifTruncate
                            maxLines={1}
                            maxWidth={
                              screenWidth
                                ? Math.round((screenWidth - 200) * 0.17)
                                : 400
                            }
                          >
                            {sku.Name}
                          </MotifTruncate>
                        </div>
                        <div
                          className="sku-number"
                          style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center",
                          }}
                        >
                          {isCheckoutPage ? (
                            <MotifTruncate
                              maxLines={1}
                              maxWidth={
                                screenWidth
                                  ? Math.round((screenWidth - 200) * 0.09)
                                  : 133
                              }
                            >
                              {sku.SkuId}
                            </MotifTruncate>
                          ) : (
                            <span>{sku.SkuId}</span>
                          )}

                          {isOrderpipelineOrderTab &&
                            renderSkuTypeBadge(sku.SkuType)}
                        </div>
                      </div>
                    </li>
                  );
                } else {
                  return (
                    <>
                      <li key={index}>
                        <div
                          className={`sku-cell ${
                            skuError && showRetiredError ? "sku-error" : ""
                          }`}
                        >
                          <div className="sku-cell-number">
                            {skuError && showRetiredError && (
                              <MotifIcon
                                src={IconoirWarningCircle}
                                className="warning-icon"
                              />
                            )}
                            <MotifTruncate
                              maxLines={1}
                              maxWidth={
                                screenWidth
                                  ? Math.round((screenWidth - 200) * 0.17)
                                  : 400
                              }
                            >
                              {sku.Name}
                            </MotifTruncate>
                          </div>
                          <div
                            className="sku-number"
                            style={{
                              display: "flex",
                              gap: "6px",
                              alignItems: "center",
                            }}
                          >
                            {isCheckoutPage ? (
                              <MotifTruncate
                                maxLines={1}
                                maxWidth={
                                  screenWidth
                                    ? Math.round((screenWidth - 200) * 0.09)
                                    : 133
                                }
                              >
                                {sku.SkuId}
                              </MotifTruncate>
                            ) : (
                              <span>{sku.SkuId}</span>
                            )}
                            {isOrderpipelineOrderTab &&
                              renderSkuTypeBadge(sku.SkuType)}
                          </div>
                        </div>
                      </li>
                      <ul>
                        {sku.SkuRelation.map((skuRelation: any) => {
                          const skuError =
                            skuRelation?.StatusCd === "sku_retired";
                          return (
                            <li key={skuRelation.SkuId}>
                              <div
                                className={`sku-cell ${
                                  skuError && showRetiredError
                                    ? "sku-error"
                                    : ""
                                }`}
                              >
                                <div className="sku-cell-number">
                                  {skuError && showRetiredError && (
                                    <MotifIcon
                                      src={IconoirWarningCircle}
                                      className="warning-icon"
                                    />
                                  )}
                                  <MotifTruncate
                                    maxLines={1}
                                    maxWidth={
                                      screenWidth
                                        ? Math.round((screenWidth - 200) * 0.12)
                                        : 200
                                    }
                                  >
                                    {skuRelation.Name}
                                  </MotifTruncate>
                                </div>
                                <div
                                  className="sku-number"
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "center",
                                  }}
                                >
                                  {isCheckoutPage ? (
                                    <MotifTruncate
                                      maxLines={1}
                                      maxWidth={
                                        screenWidth
                                          ? Math.round(
                                              (screenWidth - 200) * 0.07,
                                            )
                                          : 100
                                      }
                                    >
                                      {skuRelation.SkuId}
                                    </MotifTruncate>
                                  ) : (
                                    <span>{skuRelation.SkuId}</span>
                                  )}

                                  {isOrderpipelineOrderTab &&
                                    renderSkuTypeBadge(skuRelation.SkuType)}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  );
                }
              })}
            </ul>
          </ul>
        </td>
        {showEngagementColumn && (
          <td>
            <div
              className="table-cell row-amounts empty-cell"
              style={{ display: !isExpand ? "none" : "block" }}
            ></div>
            {detail.map((sku: any, index: number) => {
              if (sku.SkuRelation.length === 0) {
                return (
                  <div
                    key={index}
                    className="table-cell"
                    style={{ display: !isExpand ? "none" : "block" }}
                  >
                    <div>{getEngagementCodeText(sku)}</div>
                    <div className="sku-number">
                      {getEngagementNameText(sku)}
                    </div>
                  </div>
                );
              } else {
                return (
                  <>
                    <div
                      key={index}
                      className="table-cell row-amounts"
                      style={{ display: !isExpand ? "none" : "block" }}
                    >
                      <div>{getEngagementCodeText(sku)}</div>
                      <div className="sku-number">
                        {getEngagementNameText(sku)}
                      </div>
                    </div>
                    {sku.SkuRelation.map((skuRelation: any) => {
                      return (
                        <div
                          key={index}
                          className="table-cell"
                          style={{ display: !isExpand ? "none" : "block" }}
                        >
                          <div>{getEngagementCodeText(skuRelation)}</div>
                          <div className="sku-number">
                            {getEngagementNameText(skuRelation)}
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              }
            })}
          </td>
        )}
        {showQuantityColumn && (
          <td>
            <div
              className="table-cell row-amounts empty-cell"
              style={{ display: !isExpand ? "none" : "block" }}
            ></div>
            {detail.map((sku: any, index: number) => {
              if (sku.SkuRelation.length === 0) {
                return (
                  <div
                    key={index}
                    className="table-cell"
                    style={{ display: !isExpand ? "none" : "block" }}
                  >
                    {sku.CostIntervalCd == "onetime" ? (
                      <>
                        <span>01</span>
                        <div className="sku-number hideWithSpace">
                          {sku.SkuId}
                        </div>
                      </>
                    ) : (
                      <>
                        <span>{sku.OrderQuantity}</span>
                        <div className="sku-number hideWithSpace">
                          {sku.SkuId}
                        </div>
                      </>
                    )}
                  </div>
                );
              } else {
                return (
                  <>
                    <div
                      key={index}
                      className="table-cell row-amounts"
                      style={{ display: !isExpand ? "none" : "block" }}
                    >
                      <div>
                        {sku.CostIntervalCd == "onetime" ? (
                          <>
                            <span>01</span>
                            <div className="sku-number hideWithSpace">
                              {sku.SkuId}
                            </div>
                          </>
                        ) : (
                          <>
                            <span>{sku.OrderQuantity}</span>
                            <div className="sku-number hideWithSpace">
                              {sku.SkuId}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {sku.SkuRelation.map((skuRelation: any) => {
                      return (
                        <div
                          className="table-cell"
                          style={{ display: !isExpand ? "none" : "block" }}
                        >
                          <div>
                            {skuRelation.CostIntervalCd == "onetime" ? (
                              <>
                                <span>01</span>
                                <div className="sku-number hideWithSpace">
                                  {sku.SkuId}
                                </div>
                              </>
                            ) : (
                              <>
                                <span>{skuRelation.OrderQuantity}</span>
                                <div className="sku-number hideWithSpace">
                                  {sku.SkuId}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              }
            })}
          </td>
        )}
        <td className={isCheckoutPage ? "unit-cost-col" : ""}>
          <div
            className="table-cell row-amounts empty-cell"
            style={{ display: !isExpand ? "none" : "block" }}
          ></div>
          {detail.map((sku: any, index: number) => {
            if (sku.SkuRelation.length === 0) {
              return (
                <div
                  key={index}
                  className={`table-cell ${
                    isOrderpipelineOrderTab ? "text-right-align" : ""
                  }`}
                  style={{ display: !isExpand ? "none" : "block" }}
                >
                  <div>
                    {sku.CostMetricCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  <div
                    className={`sku-number ${
                      isOrderpipelineOrderTab ? "text-right-align" : ""
                    }`}
                  >
                    {isOrderpipelineOrderTab
                      ? getCostIntervalForCostMetric(
                          sku.CostIntervalCd,
                          sku.CostMetricCd,
                        )
                      : getCostInterval(sku.CostIntervalCd)}
                  </div>
                </div>
              );
            } else {
              return (
                <>
                  <div
                    key={index}
                    className={`table-cell row-amounts ${
                      isOrderpipelineOrderTab ? "text-right-align" : ""
                    }`}
                    style={{ display: !isExpand ? "none" : "block" }}
                  >
                    <div>
                      {sku.CostMetricCost.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      className={`sku-number ${
                        isOrderpipelineOrderTab ? "text-right-align" : ""
                      }`}
                    >
                      {isOrderpipelineOrderTab
                        ? getCostIntervalForCostMetric(
                            sku.CostIntervalCd,
                            sku.CostMetricCd,
                          )
                        : getCostInterval(sku.CostIntervalCd)}
                    </div>
                  </div>
                  {sku.SkuRelation.map((skuRelation: any) => {
                    return (
                      <div
                        className={`table-cell ${
                          isOrderpipelineOrderTab ? "text-right-align" : ""
                        }`}
                        style={{ display: !isExpand ? "none" : "block" }}
                      >
                        <div>
                          {skuRelation.CostMetricCost.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}
                        </div>
                        <div
                          className={`sku-number ${
                            isOrderpipelineOrderTab ? "text-right-align" : ""
                          }`}
                        >
                          {isOrderpipelineOrderTab
                            ? getCostIntervalForCostMetric(
                                skuRelation.CostIntervalCd,
                                skuRelation.CostMetricCd,
                              )
                            : getCostInterval(skuRelation.CostIntervalCd)}
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            }
          })}
        </td>
        {!showQuantityColumn && (
          <td className={isCheckoutPage ? "units-per-period-col" : ""}>
            <div
              className="table-cell row-amounts empty-cell"
              style={{ display: !isExpand ? "none" : "block" }}
            ></div>
            {detail.map((sku: any, index: number) => {
              if (sku.SkuRelation.length === 0) {
                return (
                  <div
                    key={index}
                    className="table-cell"
                    style={{ display: !isExpand ? "none" : "block" }}
                  >
                    {sku.CostIntervalCd == "onetime" ? (
                      <>
                        <span>1</span>
                        <div className="sku-number">
                          {getUnitsUsedForPeriod(
                            sku.CostIntervalCd,
                            sku.CostMetricCd,
                            1,
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <span>{sku.OrderQuantity}</span>
                        <div className="sku-number">
                          {isCheckoutPage ? (
                            <MotifTruncate
                              maxLines={1}
                              maxWidth={
                                screenWidth
                                  ? Math.round((screenWidth - 200) * 0.07)
                                  : 65
                              }
                            >
                              {getUnitsUsedForPeriod(
                                sku.CostIntervalCd,
                                sku.CostMetricCd,
                                sku.OrderQuantity,
                              )}
                            </MotifTruncate>
                          ) : (
                            getUnitsUsedForPeriod(
                              sku.CostIntervalCd,
                              sku.CostMetricCd,
                              sku.OrderQuantity,
                            )
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              } else {
                return (
                  <>
                    <div
                      key={index}
                      className="table-cell row-amounts"
                      style={{ display: !isExpand ? "none" : "block" }}
                    >
                      <div>
                        {sku.CostIntervalCd == "onetime" ? (
                          <>
                            <span>1</span>
                            <div className="sku-number">
                              {getUnitsUsedForPeriod(
                                sku.CostIntervalCd,
                                sku.CostMetricCd,
                                1,
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <span>{sku.OrderQuantity}</span>
                            <div className="sku-number">
                              {getUnitsUsedForPeriod(
                                sku.CostIntervalCd,
                                sku.CostMetricCd,
                                sku.OrderQuantity,
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {sku.SkuRelation.map((skuRelation: any) => {
                      return (
                        <div
                          className="table-cell"
                          style={{ display: !isExpand ? "none" : "block" }}
                        >
                          <div>
                            {skuRelation.CostIntervalCd == "onetime" ? (
                              <>
                                <span>1</span>
                                <div className="sku-number">Unit</div>
                              </>
                            ) : (
                              <>
                                <span>{skuRelation.OrderQuantity}</span>
                                <div className="sku-number">
                                  {getUnitsUsedForPeriod(
                                    skuRelation.CostIntervalCd,
                                    skuRelation.CostMetricCd,
                                    skuRelation.OrderQuantity,
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              }
            })}
          </td>
        )}
        <td className={isCheckoutPage ? "periods-col" : ""}>
          <div
            className="table-cell row-amounts empty-cell"
            style={{ display: !isExpand ? "none" : "block" }}
          ></div>
          {detail.map((sku: any, index: number) => {
            if (sku.SkuRelation.length === 0) {
              return (
                <div
                  key={index}
                  className="table-cell"
                  style={{ display: !isExpand ? "none" : "block" }}
                >
                  {getDuration(sku)}
                </div>
              );
            } else {
              return (
                <>
                  <div
                    key={index}
                    className="table-cell row-amounts"
                    style={{ display: !isExpand ? "none" : "block" }}
                  >
                    <div key={index} className="table-cell">
                      {getDuration(sku)}
                    </div>
                  </div>
                  {sku.SkuRelation.map((skuRelation: any) => {
                    return (
                      <div
                        className="table-cell"
                        style={{ display: !isExpand ? "none" : "block" }}
                      >
                        <div>{getDuration(skuRelation)}</div>
                      </div>
                    );
                  })}
                </>
              );
            }
          })}
        </td>
        <td className={isCheckoutPage ? "subtotal-col" : ""}>
          <div
            className={`table-cell row-amounts parent-sku ${
              isOrderpipelineOrderTab ? "text-right-align" : ""
            }`}
            style={{ display: !isExpand ? "none" : "block" }}
          >
            <div className="title" style={totalCostColorStyle}>
              {getTotalOrderAmount(detail)}
            </div>
          </div>
          {detail.map((sku: any, index: number) => {
            if (sku.SkuRelation.length === 0) {
              return (
                <div
                  key={index}
                  className={`table-cell ${
                    isOrderpipelineOrderTab ? "text-right-align" : ""
                  }`}
                  style={{ display: !isExpand ? "none" : "block" }}
                >
                  <>
                    <span>{calculateTotalCost(sku)}</span>
                    <div className="sku-number hideWithSpace">Total</div>
                  </>
                </div>
              );
            } else {
              return (
                <>
                  <div
                    key={index}
                    className={`table-cell row-amounts ${
                      isOrderpipelineOrderTab ? "text-right-align" : ""
                    }`}
                    style={{ display: !isExpand ? "none" : "block" }}
                  >
                    <div>
                      <>
                        <span>{calculateTotalCost(sku)}</span>
                        <div className="sku-number hideWithSpace">Total</div>
                      </>
                    </div>
                  </div>
                  {sku.SkuRelation.map((skuRelation: any) => {
                    return (
                      <div
                        className={`table-cell ${
                          isOrderpipelineOrderTab ? "text-right-align" : ""
                        }`}
                        style={{ display: !isExpand ? "none" : "block" }}
                      >
                        <div>
                          <>
                            <span>{calculateTotalCost(skuRelation)}</span>
                            <div className="sku-number hideWithSpace">
                              Total
                            </div>
                          </>
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            }
          })}
        </td>
      </tr>
    );
  };

  return <>{renderProductRow(skuDetails)}</>;
}
